// The 24 canonical categories of the first run, in order (build/astra/figma-onboarding.md v2, "The 24 categories";
// 10/07 Alvin: "Drinks and bars" becomes "Career and startups"). Keys match supabase/functions/_shared/categories.ts.
// `sym` is the Field signals v2 file (app/public/symbols/field-signals/<sym>.svg); the ten first-run-only keys got their
// own marks in round 3 (build/astra/symbols-r3.txt, drawn by ops/symbols.py --r3).

export type Canon = { key: string; screen: 1 | 2 | 3; sym: string; borrowed?: true }

const C = (key: string, screen: 1 | 2 | 3, sym: string, borrowed?: true): Canon => ({ key, screen, sym, ...(borrowed ? { borrowed } : {}) })

export const CANON: Canon[] = [
  C('music', 1, 'music'), C('outdoors', 1, 'outdoors'), C('theater', 1, 'theater'), C('art', 1, 'art'),
  C('comedy', 1, 'comedy'), C('market', 1, 'markets'), C('food', 1, 'food'), C('film', 1, 'film'),
  C('fitness', 2, 'fitness'), C('nightlife', 2, 'nightlife'), C('class', 2, 'classes'), C('community', 2, 'community'),
  C('talks', 2, 'talks'), C('career', 2, 'career'), C('day-trip', 2, 'day-trips'), C('history', 2, 'history'),
  C('games', 3, 'games'), C('books', 3, 'books'), C('dance', 3, 'dance'), C('science', 3, 'science'),
  C('design', 3, 'design'), C('photography', 3, 'photography'), C('wellness', 3, 'wellness'), C('sports', 3, 'sports'),
]
export const CANON_KEYS = CANON.map((c) => c.key)
const BY_KEY = new Map(CANON.map((c) => [c.key, c]))
export const canon = (key: string) => BY_KEY.get(key) ?? null

/** Pool category id -> symbol file (SYMBOLS.md APP_SLUG, reversed). */
const POOL_SYM: Record<string, string> = { drink: 'drinks', class: 'classes', market: 'markets', 'day-trip': 'day-trips' }
export const symFor = (cat: string) => BY_KEY.get(cat)?.sym ?? POOL_SYM[cat] ?? (['art', 'film', 'music', 'food', 'comedy', 'outdoors', 'talks', 'fitness', 'community', 'theater', 'nightlife', 'other'].includes(cat) ? cat : 'other')

/** The copy key for a canonical category's first-run label. */
export const canonLabelKey = (key: string) => `onb.cat.${key.replace(/-/g, '_')}`

/** Words that tick a first-run tile when someone says or types them (lead's choice; the server keeps the text too). */
const SAYS: Record<string, RegExp> = {
  music: /\b(music|gigs?|concerts?|jazz|bands?|live shows?|dj|live sets?)\b/i, // "comedy shows" is not music (B-040)
  outdoors: /\b(outdoors?|hik(e|es|ing)|trails?|nature|beach(es)?|parks?|camping)\b/i,
  theater: /\b(theat(er|re)|plays?|musicals?|improv|stage)\b/i,
  art: /\b(art|galler(y|ies)|museums?|exhibits?|painting)\b/i,
  comedy: /\b(comedy|stand-?up|comedians?|funny)\b/i,
  market: /\b(markets?|farmers|flea|makers?|vintage)\b/i,
  food: /\b(food|eat(ing)?|tacos?|restaurants?|tasting|cooking)\b/i,
  film: /\b(films?|movies?|cinema|screenings?)\b/i,
  fitness: /\b(fitness|gym|workouts?|run(ning)?|yoga|climb(ing)?|cycling|bik(e|ing))\b/i,
  nightlife: /\b(nightlife|clubs?|clubbing|late nights?|parties|party)\b/i,
  class: /\b(class(es)?|workshops?|learn(ing)?|lessons?)\b/i,
  community: /\b(community|meetups?|volunteer(ing)?|meet people|new people)\b/i,
  talks: /\b(talks?|lectures?|ideas|panels?|debates?)\b/i,
  career: /\b(career|startups?|founders?|networking|tech events?|jobs?)\b/i,
  'day-trip': /\b(day trips?|road trips?|getaways?)\b/i,
  history: /\b(history|historic(al)?|heritage)\b/i,
  games: /\b(games?|board games?|trivia|arcades?|gaming)\b/i,
  books: /\b(books?|reading|readings|authors?|book clubs?|poetry)\b/i,
  dance: /\b(danc(e|es|ing)|salsa|swing)\b/i,
  science: /\b(science|tech|technology|space|astronomy|robots?)\b/i,
  design: /\b(design|architecture|furniture)\b/i,
  photography: /\b(photo(graphy|s)?|cameras?)\b/i,
  wellness: /\b(wellness|meditation|spa|sound baths?|breathwork|calm)\b/i,
  sports: /\b(sports?|games? (at|in)|basketball|baseball|soccer|football|hockey)\b/i,
}
export function keysIn(text: string): string[] {
  return CANON_KEYS.filter((k) => SAYS[k]?.test(text))
}


/** What each first-run key means in the pool (mirrors supabase/functions/_shared/categories.ts `matches`). */
const POOL_OF: Record<string, string[]> = { nightlife: ['nightlife', 'drink'] }
const TAGS_OF: Record<string, string[]> = {
  career: ['career', 'networking', 'startup', 'tech'], history: ['history'], games: ['games'], books: ['bookish'], dance: ['dance'],
  science: ['science', 'machines'], wellness: ['wellness'], sports: ['sports'],
}
const WORDS_OF: Record<string, RegExp> = {
  career: /\b(?:startups?|founders?|tech week|demo day|hackathon\w*|pitch night|venture|vc|networking|career\w*|job fair|industry mixer|entrepreneur\w*|product managers?|ai meetup)\b/i,
  design: /\b(?:design|architect\w*|typograph\w*|furniture)\b/i,
  photography: /\bphoto(?:graph\w*|s)?\b/i,
}
const POOL_KEYS = new Set(['music', 'outdoors', 'theater', 'art', 'comedy', 'market', 'food', 'film', 'fitness', 'class', 'community', 'talks', 'day-trip'])
export function matchesKey(it: { category: string; tags?: string[] | null; title: string; one_line?: string | null }, key: string): boolean {
  if (POOL_KEYS.has(key) && it.category === key) return true
  if ((POOL_OF[key] ?? []).includes(it.category)) return true
  if ((TAGS_OF[key] ?? []).some((t) => (it.tags ?? []).includes(t))) return true
  return Boolean(WORDS_OF[key]?.test(`${it.title} ${it.one_line ?? ''}`))
}
/** The first of their interests this activity belongs to (the "you picked" chip on For you cards), or null. */
export function reasonKey(it: Parameters<typeof matchesKey>[0], interests: string[]): string | null {
  // Plan v3 blind test r3 (Dana: "You picked history" on a jazz show): the reason comes from the item's own category or
  // tags only, never a word in its title
  return interests.find((k) => (POOL_KEYS.has(k) && it.category === k) || (POOL_OF[k] ?? []).includes(it.category) || (TAGS_OF[k] ?? []).some((t) => (it.tags ?? []).includes(t))) ?? null
}
